import type { Length } from '../types';

/**
 * The conversion factors to convert various units to twips.
 */
const conversionFactorToTwips: Record<Length.Unit, number> = {
  eighthPt: 2.5,
  emu: 1 / 635,
  in: 1440,
  inch: 1440, // Legacy, used only for table indent
  pt: 20,
  px: 15,
  tw: 1,
};

export function convertUnit(value: number, from: Length.Unit, to: Length.Unit): number {
  const conversionFactor = conversionFactorToTwips[from] / conversionFactorToTwips[to];
  return value * conversionFactor;
}

export interface ConvertLengthOptions {
  /**
   * The unit to use if a plain number is given.
   * @default 'in'
   */
  legacyUnit?: Length.Unit;
}

export function convertLength(
  length: Length.Absolute,
  to: Length.Unit,
  { legacyUnit = 'in' }: ConvertLengthOptions = {}
): number {
  if (typeof length === 'number') return convertUnit(length, legacyUnit, to);
  if (length.type === 'nil') return 0;
  return convertUnit(length.value, length.type, to);
}

export interface ConvertDynamicLengthOptions<R = number> extends ConvertLengthOptions {
  convert: (length: Length.Dynamic) => R;
}

export function convertDynamicLength<R = number>(
  length: Length.Any,
  to: Length.Unit,
  { convert, ...options }: ConvertDynamicLengthOptions<R>
): number | R {
  if (typeof length === 'number') return convertLength(length, to, options);

  switch (length.type) {
    case 'auto':
    case 'pct':
      return convert(length);
    default:
      return convertLength(length, to, options);
  }
}

/* FROM INCHES TO X */
export function inToPx(inches: number): number {
  return convertUnit(inches, 'in', 'px');
}

export function inToTwips(inches: number): number {
  return convertUnit(inches, 'in', 'tw');
}

/* FROM PTs TO X */
export function ptToHalfPts(pt: number): number {
  return pt * 2;
}

export function ptToPx(pt: number): number {
  return convertUnit(pt, 'pt', 'px');
}

export function ptToTwips(pt: number): number {
  return convertUnit(pt, 'pt', 'tw');
}

export function ptToEighthPts(pt: number): number {
  return convertUnit(pt, 'pt', 'eighthPt');
}

export function eighthPtToPt(eigthPt: number): number {
  return convertUnit(eigthPt, 'eighthPt', 'pt');
}

export function pxToIn(px: number): number {
  return convertUnit(px, 'px', 'in');
}

export function ptToIn(pt: number): number {
  return convertUnit(pt, 'pt', 'in');
}

export function inToPt(inches: number): number {
  return convertUnit(inches, 'in', 'pt');
}

export function pxToPt(px: number): number {
  return convertUnit(px, 'px', 'pt');
}

export function pxToTwips(px: number): number {
  return convertUnit(px, 'px', 'tw');
}

/* FROM TWIPS TO X */
export function twipsToIn(twips: number): number {
  return convertUnit(twips, 'tw', 'in');
}

export function twipsToPt(twips: number): number {
  return convertUnit(twips, 'tw', 'pt');
}

export function twipsToPx(twips: number): number {
  return convertUnit(twips, 'tw', 'px');
}

/**
 * Line spacing as a unit/multiplier (e.g., 1, 1.15, 1.5, etc.) seems to be based off the standard line spacing in twips for a 12pt font,
 * which equates to 240 twips.  So, we must take our unit/multiplier and multiply by 240 to get the correct value in twips.
 * I think the DOCX library sets lineRule to auto on the <w:spacing /> element to make a unit/multiplier work.
 * Ex: <w:spacing w:before="360" w:after="120" w:line="480" w:lineRule="auto" w:beforeAutospacing="0" w:afterAutospacing="0"/>
 * Sources: http://officeopenxml.com/WPspacing.php and https://c-rex.net/projects/samples/ooxml/e1/Part4/OOXML_P4_DOCX_spacing_topic_ID0EA1OM.html.
 */
export function lineSpacingToTwips(lineSpacing: number): number {
  return lineSpacing * 240;
}

/**
 * ECMA 20.1.10.3 ST_Angle (Angle)
 * This simple type represents an angle in 60,000ths of a degree.
 * Positive angles are clockwise (i.e., towards the positive y axis).
 * Negative angles are counter-clockwise (i.e., towards the negative y axis).
 */
export function stAngleToDegrees(angle: number): number {
  return angle / 60000;
}

export function degreesToStAngle(degrees: number): number {
  return degrees * 60000;
}

export function emuToPx(emu: number): number {
  return convertUnit(emu, 'emu', 'px');
}

export function pxToEmu(px: number): number {
  return convertUnit(px, 'px', 'emu');
}
